import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSalesReportAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { useLockVersion } from '@/api/lock-version'
import { useSalesReport, useUpdateSalesReport, useVoidSalesReport } from '@/api/sales-reports'
import type { SalesReportDetail, SalesReportMovement } from '@/api/sales-reports'
import { useWorkspace } from '@/api/workspace'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { reportVoidErrorMessage } from '@/components/payments/error-message'
import { PaymentsCard } from '@/components/payments/payments-card'
import { RecordHistory } from '@/components/record-history/record-history'
import { salesReportErrorMessage } from '@/components/sales-reports/error-message'
import { StatusBadge } from '@/components/sales-reports/status-badge'
import { StaleRecordNotice } from '@/components/stale-record-notice'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Sales Report detail (UI-SCREENS S-07, 06b #185): the report number, the channel and period, the
// lines (units, Vendor Price, line amount and, for the Owner, the COGS snapshot at the time of the
// report with the line margin, 08e #206), the margin total, the totals with the difference and its
// note, the Payments, the Sold movements it posted, the note editor and the Owner's *Edit settlement
// amount* and *Void report* actions.
export const Route = createFileRoute('/w/$slug/sales-reports/$reportId/')({
  component: SalesReportDetailScreen,
  staticData: { breadcrumb: 'salesReports.detail.title' },
})

function SalesReportDetailScreen() {
  const { t } = useTranslation()
  const { slug, reportId } = Route.useParams()
  const id = Number(reportId)
  const report = useSalesReport(slug, id)
  const voidReport = useVoidSalesReport()
  const idempotency = useIdempotencyKey()
  const updateReport = useUpdateSalesReport()
  // The Owner-only History section (#187): the settlement-amount edits, the note edits, the void
  // and the voids of this report's Payments. Only the Owner may read it (the API refuses Staff),
  // and only the Owner ever sees the money, so it follows the same flag.
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useSalesReportAuditLogs(slug, id, { enabled: isOwner })

  if (report.isPending) return <LoadingSkeleton variant="page" />
  if (report.isError || !report.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data: SalesReportDetail = report.data
  const createdBy = data.created_by ? (data.created_by.name ?? data.created_by.email) : '—'

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidReport.mutate(
      { params: { path: { slug, sales_report_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateReport.mutate(
      { params: { path: { slug, id } }, body: { sales_report: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  const onSaveSettlement = (amount: number, differenceNote: string | null, save: NoteSave) => {
    updateReport.mutate(
      {
        params: { path: { slug, id } },
        body: {
          sales_report: {
            settlement_amount: amount,
            difference_note: differenceNote,
            lock_version: save.lockVersion,
          },
        },
      },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-3xl flex-col gap-4 ${voidedClass(Boolean(data.voided_at))}`}>
      <Link to="/w/$slug/sales-reports" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('salesReports.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-xl font-semibold">{data.sales_report_no}</h1>
        <StatusBadge report={data} />
        <VoidedBadge record={data} />
        <div className="ml-auto flex flex-wrap gap-2">
          {data.can.void ? (
            <VoidDialog
              onVoid={onVoid}
              pending={voidReport.isPending}
              errorMessage={voidReport.isError ? reportVoidErrorMessage(t, voidReport.error) : ''}
              onReset={voidReport.reset}
              blocked={voidReport.isError ? <DependentRecords slug={slug} error={voidReport.error} /> : null}
            />
          ) : null}
        </div>
      </div>
      <VoidedNotice record={data} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <Figure label={t('salesReports.columns.channel')} value={data.sales_channel.name} />
        <Figure
          label={t('salesReports.columns.period')}
          value={t('salesReports.period', {
            from: formatBusinessDate(data.period_start),
            to: formatBusinessDate(data.period_end),
          })}
        />
        <Figure label={t('salesReports.columns.date')} value={formatBusinessDate(data.occurred_on)} />
        <Figure label={t('salesReports.columns.units')} value={t('salesReports.units', { count: data.total_units })} />
        <Figure label={t('salesReports.columns.createdBy')} value={createdBy} />
      </dl>
      {data.period_clamped ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('salesReports.detail.periodClamped', { date: formatBusinessDate(data.counted_period_start) })}
        </p>
      ) : null}

      {data.expected_amount === undefined ? null : (
        <Totals
          data={data}
          pending={updateReport.isPending}
          errorMessage={updateReport.isError ? salesReportErrorMessage(t, updateReport.error) : ''}
          onSave={onSaveSettlement}
          onReset={updateReport.reset}
        />
      )}

      <PaymentsCard slug={slug} report={data} />

      <NoteCard
        note={data.note}
        lockVersion={data.lock_version}
        canEdit={data.can.edit_note}
        pending={updateReport.isPending}
        errorMessage={updateReport.isError ? correctionErrorMessage(t, updateReport.error) : ''}
        onSave={onSaveNote}
        onReset={updateReport.reset}
      />

      <h2 className="font-heading text-lg font-semibold">{t('salesReports.detail.lines')}</h2>
      <Card>
        <CardContent className="py-4">
          {data.lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('salesReports.detail.noLines')}</p>
          ) : (
            <ul className="flex flex-col divide-y">
              {data.lines.map((line) => (
                <li key={line.id} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">
                      {line.variant.sku} · {line.variant.name}
                    </span>
                    {/* The flag names the prefilled Vendor Price the entered one replaced (#262). */}
                    {line.price_changed ? (
                      <Badge variant="outline">
                        {line.prefilled_vendor_price === undefined
                          ? t('salesReports.priceChanged')
                          : t('salesReports.detail.priceChangedFrom', { amount: formatMoney(line.prefilled_vendor_price) })}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    <span>{t('salesReports.units', { count: line.units })}</span>
                    {line.vendor_price === undefined ? null : (
                      <>
                        <span className="text-muted-foreground">Rp {formatMoney(line.vendor_price)}</span>
                        <span className="font-medium">Rp {formatMoney(line.amount ?? 0)}</span>
                      </>
                    )}
                  </span>
                  {line.cogs_per_unit === undefined ? null : (
                    <span className="flex w-full flex-wrap items-baseline gap-x-3 text-sm text-muted-foreground">
                      <span>
                        {line.cogs_per_unit === null
                          ? t('salesReports.detail.noCogs')
                          : t('salesReports.detail.cogsAtReport', { amount: formatMoney(line.cogs_per_unit) })}
                      </span>
                      {/* Margin per unit: the line's Vendor Price less its COGS snapshot (#262). */}
                      {line.cogs_per_unit == null || line.vendor_price === undefined ? null : (
                        <span>
                          {t('salesReports.detail.marginPerUnit', {
                            amount: formatMoney(line.vendor_price - line.cogs_per_unit),
                          })}
                        </span>
                      )}
                      {line.margin == null ? null : (
                        <span className="font-medium text-foreground">
                          {t('salesReports.detail.margin', { amount: formatMoney(line.margin) })}
                        </span>
                      )}
                      <Link
                        to="/w/$slug/cogs/$variantId"
                        params={{ slug, variantId: String(line.variant.id) }}
                        className="underline-offset-4 hover:underline"
                      >
                        {t('salesReports.detail.cogsToday')}
                      </Link>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {data.margin_amount === undefined || data.lines.length === 0 ? null : (
            <div className="mt-3 flex flex-col gap-1 border-t pt-3 text-sm">
              <div className="flex justify-between font-medium">
                <span>{t('salesReports.detail.marginTotal')}</span>
                <span>Rp {formatMoney(data.margin_amount)}</span>
              </div>
              {data.lines_without_cogs ? (
                <p className="text-muted-foreground">
                  {t('salesReports.detail.linesWithoutCogs', { count: data.lines_without_cogs })}
                </p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <h2 className="font-heading text-lg font-semibold">{t('salesReports.detail.movements')}</h2>
      <Card>
        <CardContent className="py-4">
          {data.movements.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('salesReports.detail.noMovements')}</p>
          ) : (
            <ul className="flex flex-col divide-y">
              {data.movements.map((movement) => (
                <li key={movement.id} className="py-3 first:pt-0 last:pb-0">
                  <MovementRow report={data} movement={movement} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// The expected total, the settlement amount and the difference between them — shown, never
// blocking (owner decision 2026-09-23, Q5). The settlement amount is the one stock-adjacent field
// the Owner may still change, audited before and after (UI-SCREENS G25).
function Totals({
  data,
  pending,
  errorMessage,
  onSave,
  onReset,
}: {
  data: SalesReportDetail
  pending: boolean
  errorMessage: string
  onSave: (amount: number, differenceNote: string | null, save: NoteSave) => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [amount, setAmount] = useState(String(data.settlement_amount ?? 0))
  const [note, setNote] = useState(data.difference_note ?? '')
  const version = useLockVersion(data.lock_version)
  const expected = data.expected_amount ?? 0
  const difference = data.difference ?? 0

  // Back to the report as it is now saved, following its live lock_version again (#142).
  function restart() {
    setAmount(String(data.settlement_amount ?? 0))
    setNote(data.difference_note ?? '')
    version.reload()
    onReset()
  }

  function stopEditing() {
    setEditing(false)
    restart()
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!/^\d+$/.test(amount)) return
    onSave(Number(amount), note.trim() === '' ? null : note.trim(), {
      lockVersion: version.lockVersion,
      done: () => setEditing(false),
      settle: version.settle,
    })
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
          <Figure label={t('salesReports.columns.expected')} value={`Rp ${formatMoney(expected)}`} />
          <Figure
            label={t('salesReports.columns.settlement')}
            value={`Rp ${formatMoney(data.settlement_amount ?? 0)}`}
          />
          <Figure label={t('salesReports.columns.difference')} value={signedMoney(difference)} />
          <Figure
            label={t('salesReports.columns.outstanding')}
            value={`Rp ${formatMoney(data.outstanding_amount ?? 0)}`}
          />
        </dl>
        {difference === 0 ? null : (
          <p className="text-sm text-muted-foreground">
            {t('salesReports.detail.differenceNote', { note: data.difference_note ?? '—' })}
          </p>
        )}

        {editing ? (
          <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
            {version.stale ? (
              <StaleRecordNotice onReload={restart} />
            ) : (
              <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
                {errorMessage}
              </p>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="settlement">{t('salesReports.form.settlement')}</Label>
              <Input
                id="settlement"
                inputMode="numeric"
                className="h-11"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="difference-note">{t('salesReports.form.differenceNote')}</Label>
              <Input
                id="difference-note"
                className="h-11"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <p className="text-sm text-muted-foreground">{t('salesReports.form.differenceNoteHint')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" className="h-11" disabled={pending}>
                {t(pending ? 'salesReports.form.saving' : 'salesReports.form.save')}
              </Button>
              <Button type="button" variant="outline" className="h-11" onClick={stopEditing}>
                {t('salesReports.form.cancel')}
              </Button>
            </div>
          </form>
        ) : data.can.edit_settlement ? (
          <Button type="button" variant="outline" className="h-11 self-start" onClick={() => setEditing(true)}>
            {t('salesReports.editSettlement')}
          </Button>
        ) : (data.payments ?? []).some((payment) => !payment.voided_at) ? (
          // Frozen from the first Payment on (domain-rules Corrections, UI-SCREENS G25): the
          // Owner voids the Payments first, so the screen says why the action is gone.
          <p className="text-sm text-muted-foreground">{t('payments.settlementLocked')}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

// A Variant leaving the channel — a **Sold** movement. A void adds the exact negation of each
// row, linked to it by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ report, movement }: { report: SalesReportDetail; movement: SalesReportMovement }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium">
        {movement.variant ? `${movement.variant.sku} · ${movement.variant.name}` : '—'}
      </span>
      <span className="text-muted-foreground">{report.sales_channel.name}</span>
      {movement.reverses_movement_id ? (
        <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
      ) : null}
      <span className="ml-auto">{signedQty(movement.qty_delta)}</span>
    </div>
  )
}

const signedQty = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatQty(Math.abs(value))}`
const signedMoney = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}Rp ${formatMoney(Math.abs(value))}`

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
